import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography, } from "@mui/material";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FormActions } from "../utils/FormActions";
import { FixtureRowFields } from "./FixtureRowFields";
const blankFixture = (seed) => ({
    division: seed?.division,
    homeTeam: undefined,
    awayTeam: undefined,
    // No fabricated kick-off — first row is empty; added rows inherit the
    // previous row's (a real, user-entered value), never a guessed default.
    kickOff: seed?.kickOff ?? null,
    round: seed?.round ?? 1,
    matchType: seed?.matchType ?? "regular",
    playoffRound: null,
});
// Map validated form state to the API payload. The required fields are
// guaranteed present here because submit is blocked until they validate.
const toPayload = (f) => ({
    division: f.division,
    homeTeam: f.homeTeam,
    awayTeam: f.awayTeam,
    kickOff: f.kickOff.toISOString(),
    round: f.round,
    matchType: f.matchType,
    playoffRound: f.playoffRound ?? null,
});
export const GameweekFixturesForm = ({ divisions, onSubmit, isSubmitting = false, onCancel, }) => {
    const methods = useForm({
        defaultValues: {
            // One blank row per division, so every division starts ready to fill.
            fixtures: divisions.map((d) => blankFixture({ division: d.id })),
        },
    });
    const { control, handleSubmit, getValues, formState: { errors }, } = methods;
    const { fields, append, remove } = useFieldArray({
        control,
        name: "fixtures",
    });
    const showDivisionHeaders = divisions.length > 1;
    // Add a fixture to a division section, inheriting round/type/kick-off from the
    // last fixture already in that division so a run of fixtures is quick to enter.
    const addFixtureToDivision = (divisionId) => {
        const inDivision = getValues("fixtures").filter((f) => f.division === divisionId);
        const last = inDivision[inDivision.length - 1];
        append(blankFixture({
            division: divisionId,
            round: last?.round,
            matchType: last?.matchType,
            kickOff: last?.kickOff,
        }));
    };
    return (_jsx(FormProvider, { ...methods, children: _jsxs(Box, { component: "form", onSubmit: handleSubmit((data) => onSubmit(data.fixtures.map(toPayload))), noValidate: true, sx: { mt: 1 }, children: [_jsx(Stack, { spacing: 3, children: divisions.map((division) => {
                        const rows = fields
                            .map((field, index) => ({ field, index }))
                            .filter(({ field }) => field.division === division.id);
                        return (_jsxs(Box, { children: [showDivisionHeaders && (_jsx(Typography, { variant: "subtitle1", fontWeight: 600, gutterBottom: true, children: division.name })), _jsxs(Stack, { spacing: 2, children: [rows.map(({ field, index }) => (_jsx(Paper, { variant: "outlined", sx: { p: 2 }, children: _jsxs(Stack, { direction: "row", alignItems: "flex-start", spacing: 1, children: [_jsx(Box, { sx: { flexGrow: 1 }, children: _jsx(FixtureRowFields, { errors: errors.fixtures?.[index], divisions: divisions, namePrefix: `fixtures.${index}.`, showDivision: false, horizontal: true }) }), _jsx(Tooltip, { title: "Remove fixture", children: _jsx("span", { children: _jsx(IconButton, { "aria-label": "Remove fixture", onClick: () => remove(index), disabled: fields.length === 1, children: _jsx(DeleteIcon, {}) }) }) })] }) }, field.id))), rows.length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Add your first fixture" }))] }), _jsx(Button, { startIcon: _jsx(AddIcon, {}), onClick: () => addFixtureToDivision(division.id), sx: { mt: 1 }, children: "Add fixture" })] }, division.id));
                    }) }), _jsx(FormActions, { submitLabel: `Create ${fields.length} ${fields.length === 1 ? "fixture" : "fixtures"}`, isSubmitting: isSubmitting, onCancel: onCancel })] }) }));
};
